import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { triggerAuthLogout } from '@/app/providers/authEvents';
import { getSignedMediaUrl } from '@/lib/utils';
import LogoutConfirmationModal from '@/features/user/components/LogoutConfirmationModal';

interface ProfileDropdownProps {
  variant?: 'main' | 'club';
  className?: string;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ 
  variant = 'main',
  className = '' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogoutClick = () => {
    // Close dropdown first
    setIsOpen(false);
    // Open logout confirmation modal
    setIsLogoutModalOpen(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    
    try {
      // Clear auth store (this will call the logout API endpoint to clear cookies)
      await logout();
      
      // Trigger AuthProvider to update immediately
      triggerAuthLogout();
      
      // Navigate to home page
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if logout fails, still navigate and clear state
      triggerAuthLogout();
      navigate('/');
    } finally {
      setIsLoggingOut(false);
      setIsLogoutModalOpen(false);
    }
  };

  const handleProfileClick = () => {
    setIsOpen(false);
    navigate('/profile');
  };

  // Get user's profile picture or use default
  const getProfilePicture = () => {
    if (user?.profile_picture_id && user.profile_picture_id !== '00000000-0000-0000-0000-000000000000') {
      return getSignedMediaUrl(user.profile_picture_id, 'image').url;
    }
    // Fallback to initials-based placeholder
    return `https://api.placeholder.com/40x40/6366f1/ffffff?text=${user?.first_name?.charAt(0).toUpperCase() || 'U'}`;
  };

  const getInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    return user?.first_name?.charAt(0).toUpperCase() || 'U';
  };

  const buttonClasses = variant === 'club'
    ? "relative group p-2 rounded-lg transition-colors cursor-pointer"
    : "relative group p-2 rounded-lg transition-colors cursor-pointer";

  const dropdownClasses = variant === 'club'
    ? "absolute right-0 top-20 w-48 bg-black/40 backdrop-blur-xl border border-gray-700/50 rounded-xl shadow-2xl z-50"
    : "absolute right-0 top-20 w-48 bg-black/40 backdrop-blur-xl border border-gray-700/50 rounded-xl shadow-2xl z-50";

  const menuItemClasses = variant === 'club'
    ? "block w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
    : "block w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors";

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClasses}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Profile menu"
      >
        <div className="relative w-8 h-8">
          <div className="absolute inset-0 rounded-full bg-amber-400"></div>
          <div className="absolute inset-px rounded-full overflow-hidden border border-gray-700 dark:border-gray-300">
            <img
              src={getProfilePicture()}
              alt="Profile"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to initials if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = `<div class="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">${getInitials()}</div>`;
                }
              }}
            />
          </div>
        </div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={dropdownClasses}
          >
            <div className="py-2">
              {/* User info header */}
              <div className={`px-4 py-3 border-b ${
                variant === 'club' 
                  ? 'border-gray-800/50' 
                  : 'border-gray-800/50'
              }`}>
                <p className="text-sm font-medium text-white dark:text-white">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-400">
                  {user?.email}
                </p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={handleProfileClick}
                  className={menuItemClasses}
                >
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </div>
                </button>
                
                <button
                  onClick={handleLogoutClick}
                  className={menuItemClasses}
                >
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </div>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogoutConfirm}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
};

export default ProfileDropdown;
